import { useEffect, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import isEqual from 'lodash/isEqual';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  type ContentStep,
  CustomQuizQuestion,
  getQuizDetailsOptions,
  Plan,
  Quiz,
} from '@cfa/pathway-fe-shared/xp';
import { Card, CardContent, Divider } from '@cfa/react-components';

import { getPlanStepNameByLang } from '@/utils/stepName';

import ConfirmationModal from '@/components/popups/ConfirmationModal';
import TranslationsHeader from '@/components/popups/ManageTranslationsModal/TranslationsHeader';
import TranslationsItem from '@/components/popups/ManageTranslationsModal/TranslationsItem';
import PathwayModal from '@/components/popups/PathwayModal';

import {
  actions,
  initializeMissingProperties,
  ManageTranslationsInput,
  manageTranslationsMutableAtom,
  ManageTranslationsState,
} from './state';

import {
  TextDto,
  TranslatedTextDto,
  translateMutation,
} from '@/_generated/api-clients/content';
import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import { userPrefsAtom } from '@/atoms/userPrefs';

interface TranslationMetadata {
  id: string;
  type:
    | 'plan'
    | 'description'
    | 'quiz'
    | 'question'
    | 'answer'
    | 'taskName'
    | 'taskNote'
    | 'sectionName';
  section?: string;
  questionIndex?: number;
  answerIndex?: number;
}

interface ManageTranslationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (translatedObject: ManageTranslationsState) => void;
  translatableObject?: Partial<Plan> | Quiz;
}

const ManageTranslationsModal: React.FC<ManageTranslationsModalProps> = ({
  isOpen,
  onSave,
  onClose,
  translatableObject,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { language: userLanguage } = useAtomValue(userPrefsAtom);
  const targetLanguage = userLanguage === 'en' ? 'es' : 'en';
  const localTranslatableObject = useAtomValue(manageTranslationsMutableAtom);
  const translate = useMutation(translateMutation());
  const [step, setStep] = useState<'translations' | 'confirm-close'>(
    'translations',
  );
  const track = useAmplitudeTrack();

  const handleClose = () => {
    setStep('translations');
    onClose();
  };

  const { data: quizData } = useQuery({
    ...getQuizDetailsOptions({
      path: { quizId: translatableObject?.id ?? '' },
    }),
    enabled:
      !!translatableObject &&
      !!translatableObject.id &&
      'typeFormId' in translatableObject &&
      !!translatableObject.typeFormId &&
      isOpen,
  });

  const translationSource: ManageTranslationsInput =
    quizData ?? translatableObject;

  const isEdited = !isEqual(
    localTranslatableObject,
    initializeMissingProperties(translationSource),
  );

  useEffect(() => {
    actions.initializeTrainingPlanTranslations(translationSource);
  }, [translationSource, isOpen]);

  const populateTranslateAllData = (res: TranslatedTextDto[]) => {
    res.map(item => {
      if (!item.id) return;

      let parsedTranslationMeta: TranslationMetadata;
      try {
        parsedTranslationMeta = JSON.parse(item.id) as TranslationMetadata;
      } catch (error) {
        datadogRum.addError(error);
        return;
      }

      const sectionId = parsedTranslationMeta.section;
      const questionIndex = parsedTranslationMeta.questionIndex;
      const answerIndex = parsedTranslationMeta.answerIndex;

      switch (parsedTranslationMeta.type) {
        case 'plan':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updatePlanNameTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              targetLanguage,
            });
          }
          break;
        case 'description':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updateDescriptionTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              targetLanguage,
            });
          }
          break;
        case 'quiz':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updateQuizNameTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              targetLanguage,
            });
          }
          break;
        case 'question':
          if (questionIndex !== undefined && item.translatedText) {
            actions.updateQuestionTranslation({
              value: item.translatedText,
              questionIndex: questionIndex,
              targetLanguage,
            });
          }
          break;
        case 'answer':
          if (
            questionIndex !== undefined &&
            answerIndex !== undefined &&
            item.translatedText
          ) {
            actions.updateAnswerTranslation({
              value: item.translatedText,
              questionIndex: questionIndex,
              answerIndex: answerIndex,
              targetLanguage,
            });
          }
          break;
        case 'taskName':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updateTaskNameTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              sectionId,
              targetLanguage,
            });
          }
          break;
        case 'taskNote':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updateTaskNotesTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              sectionId,
              targetLanguage,
            });
          }
          break;
        case 'sectionName':
          if (parsedTranslationMeta.id && item.translatedText) {
            actions.updateSectionTranslation({
              id: parsedTranslationMeta.id,
              value: item.translatedText,
              targetLanguage,
            });
          }
          break;
        default:
          // TODO TOAST ERROR P20-883
          console.log('error populating translations');
      }
    });
  };

  const addTranslateObject = (
    array: TextDto[],
    id: string,
    type: TranslationMetadata['type'],
    value: string,
    section?: string,
    questionIndex?: number,
    answerIndex?: number,
  ) => {
    const metadata: TranslationMetadata = {
      id,
      type,
      section: section ?? undefined,
      questionIndex: questionIndex ?? undefined,
      answerIndex: answerIndex ?? undefined,
    };
    array.push({
      id: JSON.stringify(metadata),
      value: value,
    });
  };

  const addTranslationSteps = (
    steps: ContentStep[],
    array: TextDto[],
    section?: string,
  ) => {
    steps.map(step => {
      if (step.type === 'task') {
        addTranslateObject(
          array,
          step.id,
          'taskName',
          step.name[userLanguage] ?? '',
          section,
        );
        if (step.note?.[userLanguage]) {
          addTranslateObject(
            array,
            step.id,
            'taskNote',
            step.note[userLanguage] ?? '',
            section,
          );
        }
      }
    });
  };

  const onTranslateAll = () => {
    const textArray: TextDto[] = [];
    if (localTranslatableObject.quizId) {
      addTranslateObject(
        textArray,
        localTranslatableObject.quizId,
        'quiz',
        localTranslatableObject.name?.[userLanguage] ?? '',
      );
      localTranslatableObject.questions?.map((question, questionIndex) => {
        addTranslateObject(
          textArray,
          question.referenceId?.[userLanguage] ?? '',
          'question',
          question.question[userLanguage] ?? '',
          undefined,
          questionIndex,
        );

        question.answers.map((answer, answerIndex) => {
          addTranslateObject(
            textArray,
            answer.referenceId?.[userLanguage] ?? '',
            'answer',
            answer.answer[userLanguage] ?? '',
            undefined,
            questionIndex,
            answerIndex,
          );
        });
      });
    } else {
      // only add plan name if there is one to translate from
      if (localTranslatableObject.name?.[userLanguage]) {
        addTranslateObject(
          textArray,
          localTranslatableObject.id ?? '',
          'plan',
          localTranslatableObject.name[userLanguage],
        );
      }
      if (localTranslatableObject.description?.[userLanguage]?.trim()) {
        addTranslateObject(
          textArray,
          localTranslatableObject.id ?? '',
          'description',
          localTranslatableObject.description[userLanguage],
        );
      }
      const nonEmptySteps =
        localTranslatableObject.content?.steps?.filter(
          (step: ContentStep) => !!step.name[userLanguage],
        ) ?? [];
      addTranslationSteps(nonEmptySteps, textArray);
      const nonEmptySections =
        localTranslatableObject.content?.sections?.filter(
          section => !!section.name[userLanguage],
        );

      nonEmptySections?.map(section => {
        addTranslateObject(
          textArray,
          section.id,
          'sectionName',
          section.name[userLanguage] ?? '',
        );
        addTranslationSteps(section.steps ?? [], textArray, section.id);
      });
    }

    translate
      .mutateAsync({
        body: {
          text: textArray,
          source: userLanguage,
          target: targetLanguage,
        },
      })
      .then(res => {
        populateTranslateAllData(res);
        // fire track event
        track(AmplitudeProductEvent.TRANSLATE_ALL, {
          training_plan_id:
            localTranslatableObject.id ??
            localTranslatableObject.quizId ??
            'empty',
        });
      })
      .catch((err: unknown) => {
        datadogRum.addError(err);
      });
  };

  const onTranslate = (
    id: string,
    value: string,
    callback: (text: string) => void,
  ) => {
    const textArray = [{ id, value }];
    translate
      .mutateAsync({
        body: {
          text: textArray,
          source: userLanguage,
          target: targetLanguage,
        },
      })
      .then(res => {
        callback(res[0]?.translatedText ?? '');
        // fire ga event
        track(AmplitudeProductEvent.TRANSLATE_SINGLE, {
          training_plan_id:
            localTranslatableObject.id ??
            localTranslatableObject.quizId ??
            'empty',
        });
      })
      .catch((err: unknown) => {
        datadogRum.addError(err);
      });
  };

  const renderStep = (
    step: ContentStep,
    sectionId: string | undefined,
    index: number,
  ) => {
    switch (step.type) {
      case 'document': {
        const stepNameInUserLang = getPlanStepNameByLang(step, userLanguage);
        const stepNameInTargetLang = getPlanStepNameByLang(
          step,
          targetLanguage,
        );
        return (
          <div key={`${step.id}-${index}`}>
            <TranslationsItem
              name={stepNameInUserLang || stepNameInTargetLang}
              targetLanguage={targetLanguage}
              translation={stepNameInTargetLang}
              type="resource"
              userLanguage={userLanguage}
            />
          </div>
        );
      }
      case 'quiz':
        return (
          <div key={`${step.id}-${index}`}>
            <TranslationsItem
              name={step.name[userLanguage] ?? ''}
              targetLanguage={targetLanguage}
              translation={step.name[targetLanguage] ?? ''}
              type="quizStep"
              userLanguage={userLanguage}
            />
          </div>
        );
      case 'task':
        return (
          <div key={`${step.id}-${index}`}>
            <TranslationsItem
              id={Number(step.id)}
              name={step.name[userLanguage] ?? ''}
              notes={step.note?.[userLanguage] ?? ''}
              notesTranslation={step.note?.[targetLanguage] ?? ''}
              onChangeName={e =>
                actions.updateTaskNameTranslation({
                  id: step.id,
                  value: e.target.value,
                  sectionId: sectionId,
                  targetLanguage,
                })
              }
              onChangeNotes={e =>
                actions.updateTaskNotesTranslation({
                  id: step.id,
                  value: e.target.value,
                  sectionId: sectionId,
                  targetLanguage,
                })
              }
              onChangeUrl={(e, anIndex) =>
                actions.updateTaskUrlTranslation({
                  id: step.id,
                  value: e.target.value,
                  sectionId: sectionId,
                  index: anIndex,
                  targetLanguage,
                })
              }
              onTranslateName={() =>
                onTranslate(
                  localTranslatableObject.id ?? '',
                  step.name[userLanguage] ?? '',
                  value =>
                    actions.updateTaskNameTranslation({
                      id: step.id,
                      value,
                      sectionId: sectionId,
                      targetLanguage,
                    }),
                )
              }
              onTranslateNotes={() =>
                onTranslate(
                  localTranslatableObject.id ?? '',
                  step.note?.[userLanguage] ?? '',
                  value =>
                    actions.updateTaskNotesTranslation({
                      id: step.id,
                      value,
                      sectionId: sectionId,
                      targetLanguage,
                    }),
                )
              }
              targetLanguage={targetLanguage}
              translation={step.name[targetLanguage] ?? ''}
              type="task"
              urls={step.urls}
              userLanguage={userLanguage}
            ></TranslationsItem>
          </div>
        );
      default:
        return null;
    }
  };

  const renderQuestion = (questionData: CustomQuizQuestion, index: number) => (
    <TranslationsItem
      answers={questionData.answers}
      id={index}
      name={questionData.question[userLanguage] ?? ''}
      onChangeAnswer={(e, answerIndex) =>
        actions.updateAnswerTranslation({
          value: e.target.value,
          questionIndex: index,
          answerIndex,
          targetLanguage,
        })
      }
      onChangeName={e =>
        actions.updateQuestionTranslation({
          value: e.target.value,
          questionIndex: index,
          targetLanguage,
        })
      }
      onTranslateAnswer={answerIndex => {
        const answer = questionData.answers[answerIndex];
        onTranslate(
          answer?.referenceId?.[userLanguage] ?? '',
          answer?.answer[userLanguage] ?? '',
          value =>
            actions.updateAnswerTranslation({
              value,
              questionIndex: index,
              answerIndex,
              targetLanguage,
            }),
        );
      }}
      onTranslateName={() =>
        onTranslate(
          localTranslatableObject.quizId ?? '',
          questionData.question[userLanguage] ?? '',
          value => {
            actions.updateQuestionTranslation({
              value,
              questionIndex: index,
              targetLanguage,
            });
          },
        )
      }
      targetLanguage={targetLanguage}
      translation={questionData.question[targetLanguage] ?? ''}
      type="question"
      userLanguage={userLanguage}
    />
  );

  const tryClose = () => {
    if (isEdited) {
      setStep('confirm-close');
      return;
    }

    handleClose();
  };

  const anyTranslatableSteps = localTranslatableObject.content?.steps?.reduce(
    (acc: boolean, step: ContentStep) => {
      if (step.type === 'task') {
        return acc || !!step.name[userLanguage] || !!step.note?.[userLanguage];
      } else {
        return acc || !!step.name[userLanguage];
      }
    },
    false,
  );
  const anyTranslatableSections =
    localTranslatableObject.content?.sections?.reduce(
      (acc: boolean, section) => acc || !!section.name[userLanguage],
      false,
    ) ?? false;
  const isPlanNameTranslatable = !!localTranslatableObject.name?.[userLanguage];
  const planDescription = localTranslatableObject.description;

  const isPlanDescriptionTranslatable =
    !!planDescription?.[userLanguage]?.trim();

  const noValidTranslations =
    !anyTranslatableSteps &&
    !anyTranslatableSections &&
    !isPlanNameTranslatable &&
    !isPlanDescriptionTranslatable;
  return (
    <PathwayModal
      bodyRef={scrollRef}
      className="max-w-[660px]! lg:max-w-[840px]!"
      isOpen={isOpen}
      onClose={tryClose}
      size="lg"
      stepKey={step}
    >
      {step === 'translations' && (
        <>
          <PathwayModal.Header>
            <div className="pb-8" data-testid="EditTranslationsHeader">
              <h2 className="text-variant-h2! text-center!">
                {t('Generic.editTranslations')}
              </h2>
              <TranslationsHeader
                isTranslatePending={translate.isPending}
                noValidTranslations={noValidTranslations}
                onTranslateAll={onTranslateAll}
              />
            </div>
          </PathwayModal.Header>
          <PathwayModal.Body className="pb-8!">
            {localTranslatableObject.quizId
              ? localTranslatableObject.name && (
                  <div className="max-w-full grow">
                    <Card className="no-hover my-6! text-left!">
                      <CardContent>
                        <TranslationsItem
                          name={
                            localTranslatableObject.name[userLanguage] ?? ''
                          }
                          onChangeName={e =>
                            actions.updateQuizNameTranslation({
                              id: localTranslatableObject.quizId ?? '',
                              value: e.target.value,
                              targetLanguage,
                            })
                          }
                          onTranslateName={() =>
                            onTranslate(
                              localTranslatableObject.quizId ?? '',
                              localTranslatableObject.name?.[userLanguage] ??
                                '',
                              value =>
                                actions.updateQuizNameTranslation({
                                  id: localTranslatableObject.quizId ?? '',
                                  value: value,
                                  targetLanguage,
                                }),
                            )
                          }
                          targetLanguage={targetLanguage}
                          translation={
                            localTranslatableObject.name[targetLanguage] ?? ''
                          }
                          type={'quiz'}
                          userLanguage={userLanguage}
                        />
                      </CardContent>
                    </Card>
                    {localTranslatableObject.questions?.map(
                      (question, index) => (
                        <Card
                          key={question.referenceId?.[userLanguage]}
                          className="no-hover my-6! text-left!"
                        >
                          <CardContent>
                            {renderQuestion(question, index)}
                          </CardContent>
                        </Card>
                      ),
                    )}
                  </div>
                )
              : localTranslatableObject.name && (
                  <div className="max-w-full grow">
                    <Card className="no-hover my-6! text-left!">
                      <CardContent>
                        <TranslationsItem
                          name={
                            localTranslatableObject.name[userLanguage] ?? ''
                          }
                          onChangeName={e =>
                            actions.updatePlanNameTranslation({
                              id: localTranslatableObject.id ?? '',
                              value: e.target.value,
                              targetLanguage,
                            })
                          }
                          onTranslateName={() =>
                            onTranslate(
                              localTranslatableObject.id ?? '',
                              localTranslatableObject.name?.[userLanguage] ??
                                '',
                              value =>
                                actions.updatePlanNameTranslation({
                                  id: localTranslatableObject.id ?? '',
                                  value: value,
                                  targetLanguage,
                                }),
                            )
                          }
                          targetLanguage={targetLanguage}
                          translation={
                            localTranslatableObject.name[targetLanguage] ?? ''
                          }
                          type={'plan'}
                          userLanguage={userLanguage}
                        />
                      </CardContent>
                    </Card>
                    {planDescription && (
                      <Card className="no-hover my-6! text-left!">
                        <CardContent>
                          <TranslationsItem
                            name={planDescription[userLanguage] ?? ''}
                            onChangeName={e => {
                              actions.updateDescriptionTranslation({
                                id: localTranslatableObject.id ?? '',
                                value: e.target.value,
                                targetLanguage,
                              });
                            }}
                            onTranslateName={() =>
                              onTranslate(
                                localTranslatableObject.id ?? '',
                                planDescription[userLanguage] ?? '',
                                value => {
                                  actions.updateDescriptionTranslation({
                                    id: localTranslatableObject.id ?? '',
                                    value,
                                    targetLanguage,
                                  });
                                },
                              )
                            }
                            targetLanguage={targetLanguage}
                            translation={planDescription[targetLanguage] ?? ''}
                            type="description"
                            userLanguage={userLanguage}
                          />
                        </CardContent>
                      </Card>
                    )}
                    {localTranslatableObject.content?.steps?.map(
                      (step, index) => (
                        <Card
                          key={step.id}
                          className="no-hover my-6! text-left!"
                        >
                          <CardContent>
                            {renderStep(step, undefined, index)}
                          </CardContent>
                        </Card>
                      ),
                    )}
                    {localTranslatableObject.content?.sections?.map(section => (
                      <Card
                        key={section.id}
                        className="no-hover my-6! text-left!"
                      >
                        <CardContent>
                          <TranslationsItem
                            name={section.name[userLanguage] ?? ''}
                            onChangeName={e =>
                              actions.updateSectionTranslation({
                                id: section.id,
                                value: e.target.value,
                                targetLanguage,
                              })
                            }
                            onTranslateName={() =>
                              onTranslate(
                                localTranslatableObject.id ?? '',
                                section.name[userLanguage] ?? '',
                                value => {
                                  actions.updateSectionTranslation({
                                    id: section.id,
                                    value,
                                    targetLanguage,
                                  });
                                },
                              )
                            }
                            targetLanguage={targetLanguage}
                            translation={section.name[targetLanguage] ?? ''}
                            type="section"
                            userLanguage={userLanguage}
                          >
                            <Divider variant="fullLength" className="my-6!" />
                            <div className="flex flex-col gap-6">
                              {section.steps?.map((step, index) =>
                                renderStep(step, section.id, index),
                              )}
                            </div>
                          </TranslationsItem>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
          </PathwayModal.Body>
          <PathwayModal.Footer>
            <PathwayModal.PrimaryButton
              color="secondary"
              isDisabled={!isEdited}
              onPress={() => onSave(localTranslatableObject)}
              testId="SaveTranslationsButton"
              variant="filled"
            >
              {t('Button.save')}
            </PathwayModal.PrimaryButton>
          </PathwayModal.Footer>
        </>
      )}
      {step === 'confirm-close' && (
        <ConfirmationModal
          isStep
          bodyText={t('Generic.exitConfirmation', {
            area: t('Generic.editTranslations'),
          })}
          headerText={t('Generic.unsavedChanges')}
          isError
          primaryButtonColor="primary"
          primaryButtonHandler={handleClose}
          primaryButtonText={t('Button.discardChanges')}
          secondaryButtonHandler={() => setStep('translations')}
          secondaryButtonText={t('Button.continueEditing')}
        />
      )}
    </PathwayModal>
  );
};

export default ManageTranslationsModal;
